import { formatMinutesAsTime as formatTime } from '../../utils/format';
import { i18nService } from '../../i18n/I18nService';
function t(key, params) {
    return i18nService.t(key, params);
}
// Mirrors the server's MIN_BOOKING_MINUTES / MAX_BOOKING_MINUTES
// (server/availability.mjs) — the picker never offers an interval the server
// would reject.
export const MIN_BOOKING_MINUTES = 60;
export const MAX_BOOKING_MINUTES = 8 * 60;
// Drops windows too short to hold even the minimum booking.
export function bookableWindows(windows) {
    return windows.filter(([windowStart, windowEnd]) => windowEnd - windowStart >= MIN_BOOKING_MINUTES);
}
// "Free on DD.MM" + "step N min" header row above the picker, as on the
// public booking page. `date` is `YYYY-MM-DD`.
export function createAvailabilityStatusRow(date, stepMinutes) {
    const [, month, day] = date.split('-');
    const statusRow = document.createElement('div');
    statusRow.className = 'booking__availability-row';
    const statusText = document.createElement('span');
    statusText.className = 'booking__availability-status';
    statusText.textContent = t('booking.availability.free', { date: `${day}.${month}` });
    const stepText = document.createElement('span');
    stepText.className = 'booking__availability-step';
    stepText.textContent = t('booking.availability.step', { step: stepMinutes });
    statusRow.append(statusText, stepText);
    return statusRow;
}
// The booking__* slot picker: one chip per free window, start/end selects
// inside the chosen window, and the "Booking: 10:00–12:30, 150 min" summary
// line. Shared by the public booking page and the admin's "Change time" and
// "Book on behalf" modals. `windows` must already be filtered by
// bookableWindows. `initial` preselects an interval inside the
// window that contains it (ignored if none does). `onChange` fires after every
// selection change made by the user — not on construction, so the caller can
// mount the element first and then read `getSelection()` itself.
export function createSlotPicker(opts) {
    const { windows, stepMinutes, initial, onChange } = opts;
    const element = document.createElement('div');
    const containing = initial
        ? windows.findIndex(([windowStart, windowEnd]) => initial.start >= windowStart && initial.end <= windowEnd)
        : -1;
    let activeWindow = Math.max(containing, 0);
    let start = initial && containing >= 0 ? initial.start : null;
    let end = initial && containing >= 0 ? initial.end : null;
    const summary = document.createElement('p');
    function changed() {
        renderSummary();
        onChange({ start, end });
    }
    function render() {
        element.innerHTML = '';
        const chipGroup = document.createElement('div');
        chipGroup.className = 'booking__chip-group';
        windows.forEach(([windowStart, windowEnd], index) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'booking__chip' + (index === activeWindow ? ' booking__chip--selected' : '');
            chip.textContent = `${formatTime(windowStart)}–${formatTime(windowEnd)}`;
            chip.addEventListener('click', () => {
                activeWindow = index;
                start = null;
                end = null;
                render();
                onChange({ start, end });
            });
            chipGroup.appendChild(chip);
        });
        element.appendChild(chipGroup);
        const [windowStart, windowEnd] = windows[activeWindow];
        if (start === null || start < windowStart || start > windowEnd - MIN_BOOKING_MINUTES)
            start = windowStart;
        if (end !== null && (end < start + MIN_BOOKING_MINUTES || end > windowEnd || end > start + MAX_BOOKING_MINUTES)) {
            end = null;
        }
        const timeRow = document.createElement('div');
        timeRow.className = 'booking__time-row';
        const startSelect = createTimeSelect(windowStart, windowEnd - MIN_BOOKING_MINUTES, start, false);
        startSelect.addEventListener('change', () => {
            start = Number(startSelect.value);
            end = null;
            render();
            onChange({ start, end });
        });
        const endSelect = createTimeSelect(start + MIN_BOOKING_MINUTES, Math.min(windowEnd, start + MAX_BOOKING_MINUTES), end, true);
        endSelect.addEventListener('change', () => {
            end = Number(endSelect.value);
            changed();
        });
        const sep = document.createElement('span');
        sep.className = 'booking__time-sep';
        sep.textContent = '—';
        timeRow.append(createTimeField(t('booking.availability.start'), startSelect), sep, createTimeField(t('booking.availability.end'), endSelect));
        element.append(timeRow, summary);
        renderSummary();
    }
    function renderSummary() {
        if (start === null || end === null) {
            summary.className = 'booking__summary booking__summary--placeholder';
            summary.textContent = t('booking.summary.placeholder');
            return;
        }
        summary.className = 'booking__summary';
        summary.textContent = t('booking.summary.result', {
            start: formatTime(start),
            end: formatTime(end),
            duration: end - start,
        });
    }
    function createTimeField(labelText, select) {
        const field = document.createElement('div');
        field.className = 'booking__time-field';
        const label = document.createElement('label');
        label.className = 'booking__chip-label';
        label.textContent = labelText;
        field.append(label, select);
        return field;
    }
    function createTimeSelect(from, to, selected, withPlaceholder) {
        const select = document.createElement('select');
        select.className = 'booking__time-select';
        if (withPlaceholder) {
            const placeholder = document.createElement('option');
            placeholder.textContent = '—';
            placeholder.disabled = true;
            placeholder.selected = selected === null;
            select.appendChild(placeholder);
        }
        for (let time = from; time <= to; time += stepMinutes) {
            const option = document.createElement('option');
            option.value = String(time);
            option.textContent = formatTime(time);
            if (time === selected)
                option.selected = true;
            select.appendChild(option);
        }
        return select;
    }
    render();
    return { element, getSelection: () => ({ start, end }) };
}
